import { doorRules, hingePositions, type DoorTypeId } from '@planner/config';
import {
  doorElevation,
  securityLockPositions,
  type LockSide,
  type ManufacturedSize,
} from '@planner/shared/doors';
import { useId } from 'react';

export function ManufacturingSpecDrawing({
  name,
  type,
  size,
  lockSide,
  lockHeight,
}: {
  name: string;
  type: DoorTypeId;
  size: ManufacturedSize;
  lockSide: LockSide;
  lockHeight: number;
}) {
  const clip = useId();
  const elevation = doorElevation(type, size, lockSide, lockHeight);
  const { height, width } = elevation;
  const x = 240;
  const y = 92;
  const bottom = y + height;
  const yFromBottom = (mm: number) => bottom - (height * mm) / size.height;
  const handleX = x + elevation.handleX;
  const handleY = y + elevation.handleY;
  const [bottomHinge, middleHinge, topHinge] = hingePositions(size.height);
  const hinges = [yFromBottom(topHinge), yFromBottom(middleHinge), yFromBottom(bottomHinge)];
  const locks = type === 'security' ? securityLockPositions(lockHeight) : undefined;
  // Leave room for the rotated height value's font ascenders.
  const viewX = 52;
  const viewWidth = width + (locks ? 392 : 298);
  const dimension = (
    column: number,
    start: number,
    end: number,
    label: string,
    value: string,
    description: string[] = [],
    right = false,
    fromTop = false,
  ) => {
    const items = [
      { text: value, size: 11.7, weight: 700, fill: '#1F2937' },
      { text: label, size: 9.75, weight: 700, fill: '#4B5563' },
      ...description.map((text) => ({ text, size: 8.06, weight: 400, fill: '#374151' })),
    ];
    const gaps = items.map((item, index) =>
      index === 0 ? 0 : item.size * (index === 1 ? 1.35 : 1.2),
    );
    let textX = right
      ? column + 6 + items[0]!.size * 0.75
      : column - 6 - gaps.reduce((total, gap) => total + gap, 0);
    const textY = fromTop ? Math.min(start, end) + 7 : Math.max(start, end) - 7;
    return (
      <g key={label}>
        <g stroke={right ? '#111111' : '#4B5563'} strokeWidth="0.9">
          <line x1={column} y1={start} x2={column} y2={end} />
          <line x1={column - 4} y1={start} x2={column + 4} y2={start} />
          <line x1={column - 4} y1={end} x2={column + 4} y2={end} />
        </g>
        {items.map((item, index) => {
          textX += gaps[index]!;
          return (
            <text
              key={index}
              x={textX}
              y={textY}
              transform={`rotate(-90 ${textX} ${textY})`}
              textAnchor={fromTop ? 'end' : 'start'}
              fill={item.fill}
              fontSize={item.size}
              fontWeight={item.weight}
            >
              {item.text}
            </text>
          );
        })}
      </g>
    );
  };
  const leader = (column: number, mm: number) => (
    <line
      x1={handleX}
      y1={yFromBottom(mm)}
      x2={column}
      y2={yFromBottom(mm)}
      stroke="#111111"
      strokeWidth="0.7"
      strokeDasharray="3 3"
    />
  );
  return (
    <svg
      role="img"
      aria-label={`${name} final door dimensions drawing`}
      viewBox={`${viewX} 34 ${viewWidth} 472`}
      className="h-full max-h-full max-w-full"
      fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif"
    >
      <defs>
        <clipPath id={clip}>
          <rect {...elevation.infill.bounds} />
        </clipPath>
      </defs>
      <rect x={viewX} y="34" width={viewWidth} height="472" fill="white" />
      <g transform={`translate(${x} ${y})`}>
        <rect {...elevation.outerFrame} />
        <g clipPath={`url(#${clip})`}>
          <rect {...elevation.infill.bounds} fill={elevation.infill.fill} />
          <path
            d={elevation.infill.path}
            fill="none"
            stroke={elevation.infill.stroke}
            strokeWidth={elevation.infill.strokeWidth}
          />
        </g>
        <rect {...elevation.innerFrame} />
        {elevation.hardware.map((rectangle, index) => (
          <rect key={index} {...rectangle} />
        ))}
      </g>
      {dimension(x - 150, y, bottom, 'DOOR HEIGHT', `${size.height}mm`)}
      {dimension(x - 88, hinges[1], bottom, 'CENTRE HINGE', `${Math.round(middleHinge)}mm`, [
        '(From the bottom of the door',
        'to the centre of the hinge)',
      ])}
      {dimension(
        x - 30,
        y,
        hinges[0],
        'TOP HINGE',
        `${doorRules.hinges.edgeOffsetMm}mm`,
        ['(From the top of the door', 'to the centre of the hinge)'],
        false,
        true,
      )}
      {dimension(x - 30, hinges[2], bottom, 'BOTTOM HINGE', `${doorRules.hinges.edgeOffsetMm}mm`, [
        '(From the bottom of the door',
        'to the centre of the hinge)',
      ])}
      {dimension(
        x + width + (locks ? 95 : 40),
        handleY,
        bottom,
        'HANDLE HEIGHT',
        `${lockHeight}mm`,
        ['(From the bottom of the door to the', 'centre of the handle / lock tongue)'],
        true,
      )}
      {leader(x + width + (locks ? 95 : 40), lockHeight)}
      {locks?.bottom !== undefined && (
        <>
          {dimension(
            x + width + 40,
            yFromBottom(locks.bottom),
            bottom,
            'BOTTOM LOCK HEIGHT',
            `${locks.bottom}mm`,
            [],
            true,
          )}
          {leader(x + width + 40, locks.bottom)}
        </>
      )}
      {locks && (
        <>
          {dimension(
            x + width + 165,
            yFromBottom(locks.top),
            bottom,
            'TOP LOCK HEIGHT',
            `${locks.top}mm`,
            [],
            true,
          )}
          {leader(x + width + 165, locks.top)}
        </>
      )}
      <g stroke="#4B5563" strokeWidth="0.9">
        <line x1={x} y1={y - 26} x2={x + width} y2={y - 26} />
        <line x1={x} y1={y - 30} x2={x} y2={y - 22} />
        <line x1={x + width} y1={y - 30} x2={x + width} y2={y - 22} />
        <line x1={x} y1={y - 26} x2={x} y2={y} strokeWidth="0.5" strokeDasharray="2 2" />
        <line
          x1={x + width}
          y1={y - 26}
          x2={x + width}
          y2={y}
          strokeWidth="0.5"
          strokeDasharray="2 2"
        />
      </g>
      <text
        x={x + width / 2}
        y={y - 44}
        textAnchor="middle"
        fill="#1F2937"
        fontSize="11.7"
        fontWeight="700"
      >
        {size.width}mm
      </text>
      <text
        x={x + width / 2}
        y={y - 31}
        textAnchor="middle"
        fill="#4B5563"
        fontSize="9.75"
        fontWeight="700"
      >
        DOOR WIDTH
      </text>
      <text
        x={viewX + viewWidth / 2}
        y={bottom + 38}
        textAnchor="middle"
        fill="#9CA3AF"
        fontSize="9.1"
      >
        Not to scale - Manufacture to the stated dimensions
      </text>
    </svg>
  );
}
